/**
 * 视频参考上传节点 - 用于视频生成的参考视频
 */
import React, { memo, useState, useCallback } from 'react';
import { useReactFlow } from '@xyflow/react';
import { NodeLayout } from './NodeLayout';
import { useProject } from '../providers/ProjectProvider';
import { useDropzone } from 'react-dropzone';
import ApiService from '../../../api/ApiService';
import { toast } from 'react-toastify';
import './VideoReferenceNode.css';

const apiService = new ApiService();

export const VideoReferenceNode = memo(({ id, data }) => {
  const { updateNodeData } = useReactFlow();
  const { readOnly } = useProject();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  // 判断是否为本地文件URL（data: 或 blob:）
  const isLocalFileUrl = (url) => {
    if (!url) return false;
    return url.startsWith('data:') || url.startsWith('blob:');
  };

  // 处理视频上传
  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      setUploading(true);
      setUploadProgress(0);
      setError(null);

      try {
        // 1. 先显示本地预览
        const reader = new FileReader();
        reader.onload = () => {
          const previewUrl = reader.result;
          updateNodeData(id, {
            videoUrl: previewUrl,
            fileName: file.name,
            isUploading: true,
          });
        };
        reader.readAsDataURL(file);

        setUploadProgress(30);
        console.log('开始上传视频到R2...', file.name);

        // 2. 上传到 Cloudflare R2 (使用通用的上传方法)
        const uploadResult = await apiService.uploadImageToR2(file);
        
        setUploadProgress(90);
        console.log('上传成功，公开URL:', uploadResult.public_url);

        // 3. 更新节点数据为云端URL
        updateNodeData(id, {
          videoUrl: uploadResult.public_url,
          fileName: file.name,
          fileKey: uploadResult.file_key,
          isUploading: false,
        });

        setUploadProgress(100);
        toast.success('视频上传成功');
      } catch (error) {
        console.error('上传失败:', error);
        const errorMessage = error.message || '上传失败，请重试';
        setError(errorMessage);
        toast.error(errorMessage);
        
        // 清除预览视频
        updateNodeData(id, {
          videoUrl: undefined,
          fileName: undefined,
          fileKey: undefined,
          isUploading: false,
        });
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    },
    [id, updateNodeData]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.mov', '.avi', '.webm', '.mkv'],
    },
    maxFiles: 1,
    disabled: readOnly,
  });

  // 清除视频
  const handleClear = () => {
    if (readOnly) return;
    updateNodeData(id, {
      videoUrl: undefined,
      fileName: undefined,
      fileKey: undefined,
      isUploading: false,
    });
    setError(null);
  };

  return (
    <NodeLayout 
      id={id} 
      type="videoReference" 
      title="视频参考"
      showInputHandle={false}
    >
      <div className="VideoReferenceNode-wrapper">
        {/* 错误提示 */}
        {error && (
          <div className="VideoReferenceNode-error">
            {error}
          </div>
        )}
        
        {data.videoUrl ? (
          // 显示已上传的视频
          <div className="VideoReferenceNode-preview">
            <video
              src={data.videoUrl}
              className="VideoReferenceNode-video"
              controls
              preload="metadata"
            >
              您的浏览器不支持视频播放
            </video>
            {!data.isUploading && !readOnly && (
              <button
                onClick={handleClear}
                className="VideoReferenceNode-clear"
                title="删除视频"
              >
                ×
              </button>
            )}
            <div className="VideoReferenceNode-info">
              {data.isUploading && (
                <div className="VideoReferenceNode-upload-status">
                  <div className="VideoReferenceNode-loading-small" />
                  <span>上传中...</span>
                </div>
              )}
              {data.fileName && (
                <div className="VideoReferenceNode-filename">
                  {data.fileName}
                </div>
              )}
              {!data.isUploading && isLocalFileUrl(data.videoUrl) && (
                <div className="VideoReferenceNode-warning">
                  ⚠️ 本地预览（未上传）
                </div>
              )}
            </div>
          </div>
        ) : (
          // 上传区域
          <div
            {...getRootProps()}
            className={`VideoReferenceNode-dropzone ${isDragActive ? 'VideoReferenceNode-dropzone-active' : ''}`}
          >
            <input {...getInputProps()} />
            <div className="VideoReferenceNode-dropzone-content">
              {uploading ? (
                <>
                  <div className="VideoReferenceNode-loading" />
                  <div className="VideoReferenceNode-text">
                    上传中 {uploadProgress > 0 ? `${uploadProgress}%` : '...'}
                  </div>
                </>
              ) : (
                <>
                  <svg className="VideoReferenceNode-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <rect x="4" y="6" width="24" height="20" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 12 L20 16 L12 20 Z" fill="currentColor"/>
                  </svg>
                  <div className="VideoReferenceNode-text">
                    {isDragActive ? '放开以上传视频' : '拖拽视频到这里或点击上传'}
                  </div>
                  <div className="VideoReferenceNode-hint">
                    支持 MP4、MOV、WEBM 等格式
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </NodeLayout>
  );
});

VideoReferenceNode.displayName = 'VideoReferenceNode';


